// src/utils/bbcode.utils.js
/**
 * Utilitaire amélioré pour convertir le BBCode en HTML
 */

/**
 * Convertit le BBCode en HTML avec support étendu des balises et gestion des retours chariot
 * @param {string} bbcode - Texte au format BBCode
 * @returns {string} Texte converti en HTML
 */
export const bbcodeToHtml = (text) => {
  if (!text) return '';
  
  let html = text;
  
  // Remplacer les caractères \n littéraux par de vrais retours à la ligne
  html = html.replace(/\\n/g, '\n');
  
  // Traitement des balises imbriquées pour éviter les problèmes de conversion
  
  // 1. Conversion des tags b et color (souvent combinés) - règle spécifique pour RGB
  html = html.replace(/\[b\]\[color=rgb\((\d+),\s*(\d+),\s*(\d+)\)\](.*?)\[\/color\]\[\/b\]/gi, '<strong style="color: rgb($1, $2, $3);">$4</strong>');
  html = html.replace(/\[b\]\[color=([^[]+)\](.*?)\[\/color\]\[\/b\]/gi, '<strong style="color: $1;">$2</strong>');
  
  // 2. Conversion des balises standard
  const bbcodeRules = [
    // Formatage de texte basique
    { pattern: /\[b\](.*?)\[\/b\]/gi, replacement: '<strong>$1</strong>' },
    { pattern: /\[i\](.*?)\[\/i\]/gi, replacement: '<em>$1</em>' },
    { pattern: /\[u\](.*?)\[\/u\]/gi, replacement: '<span style="text-decoration: underline;">$1</span>' },
    { pattern: /\[s\](.*?)\[\/s\]/gi, replacement: '<span style="text-decoration: line-through;">$1</span>' },
    
    // Traitement des couleurs (avec différents formats)
    { pattern: /\[color=rgb\((\d+),\s*(\d+),\s*(\d+)\)\](.*?)\[\/color\]/gi, replacement: '<span style="color: rgb($1, $2, $3);">$4</span>' },
    { pattern: /\[color=rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)\](.*?)\[\/color\]/gi, replacement: '<span style="color: rgba($1, $2, $3, $4);">$5</span>' },
    { pattern: /\[color=([#\w]+)\](.*?)\[\/color\]/gi, replacement: '<span style="color: $1;">$2</span>' },
    
    // Alignement
    { pattern: /\[center\](.*?)\[\/center\]/gi, replacement: '<div style="text-align: center;">$1</div>' },
    { pattern: /\[left\](.*?)\[\/left\]/gi, replacement: '<div style="text-align: left;">$1</div>' },
    { pattern: /\[right\](.*?)\[\/right\]/gi, replacement: '<div style="text-align: right;">$1</div>' },
    
    // Liens et images
    { pattern: /\[url\](.*?)\[\/url\]/gi, replacement: '<a href="$1" target="_blank">$1</a>' },
    { pattern: /\[url=(.*?)\](.*?)\[\/url\]/gi, replacement: '<a href="$1" target="_blank">$2</a>' },
    { pattern: /\[img\](.*?)\[\/img\]/gi, replacement: '<img src="$1" alt="" />' },
    
    // Citations
    { pattern: /\[quote\](.*?)\[\/quote\]/gi, replacement: '<blockquote>$1</blockquote>' },
    { pattern: /\[quote=(.*?)\](.*?)\[\/quote\]/gi, replacement: '<blockquote><cite>$1 a écrit:</cite>$2</blockquote>' },
  ];
  
  // Appliquer les règles standard
  bbcodeRules.forEach(rule => {
    html = html.replace(rule.pattern, rule.replacement);
  });
  
  // Traitement spécial pour les listes avec attributs
  // Convertir [ml] en div (marqueur de liste)
  html = html.replace(/\[ml\](.*?)\[\/ml\]/gis, '<div class="customlist">$1</div>');
  
  // Convertir [ul] en ul
  html = html.replace(/\[ul\](.*?)\[\/ul\]/gis, '<ul>$1</ul>');
  
  // Convertir les [li] avec attributs
  html = html.replace(/\[li\s+indent=(\d+)\s+align=(\w+)\](.*?)\[\/li\]/gi, 
    (match, indent, align, content) => {
      const indentStyle = indent > 0 ? `margin-left: ${indent * 20}px;` : '';
      const alignStyle = `text-align: ${align};`;
      return `<li style="${indentStyle} ${alignStyle}">${content}</li>`;
    }
  );
  
  // Convertir les [li] simples
  html = html.replace(/\[li\](.*?)\[\/li\]/gi, '<li>$1</li>');
  
  // Gestion des retours chariot (\n)
  // Traiter les doubles retours chariot comme des nouveaux paragraphes
  html = html.replace(/\n\n+/g, '</p><p>');
  
  // Traiter les retours chariot simples comme des <br>
  html = html.replace(/\n/g, '<br>');
  
  // Entourer le contenu avec des balises <p> s'il n'est pas déjà dans un paragraphe
  if (!html.startsWith('<p>')) {
    html = '<p>' + html + '</p>';
  }
  
  return html;
};

/**
 * Vérifie si le texte contient du BBCode
 * @param {string} text - Texte à vérifier
 * @returns {boolean} Vrai si le texte contient du BBCode
 */
export const containsBBCode = (text) => {
  if (!text) return false;
  
  // Liste étendue des balises à détecter
  const bbcodeTags = [
    'b', 'i', 'u', 's', 'color', 'center', 'left', 'right', 
    'url', 'img', 'quote', 'ml', 'ul', 'li'
  ];
  
  // Créer un pattern pour détecter n'importe quelle balise
  const tagPattern = bbcodeTags.join('|');
  const bbcodePattern = new RegExp(`\\[(${tagPattern})(=|\\s+|\\]|$)`, 'i');
  
  return bbcodePattern.test(text);
};

/**
 * Met à jour automatiquement un commentaire en convertissant le BBCode en HTML
 * @param {string} commentId - ID du commentaire à mettre à jour
 * @param {string} bbcodeContent - Contenu BBCode à convertir et sauvegarder
 * @param {Function} saveCallback - Fonction de sauvegarde (API)
 * @returns {Promise<string>} Contenu HTML converti
 */
export const updateCommentWithBBCode = async (commentId, bbcodeContent, saveCallback) => {
  try {
    // Convertir le BBCode en HTML
    const htmlContent = bbcodeToHtml(bbcodeContent);
    
    // Sauvegarder le contenu converti
    if (saveCallback && typeof saveCallback === 'function') {
      await saveCallback(htmlContent);
    }
    
    return htmlContent;
  } catch (error) {
    console.error('Erreur lors de la conversion et mise à jour du BBCode:', error);
    throw error;
  }
};